import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ProductTransactionContext } from '../../../../../context/ProductTransactionContext';
import Table from '../../../../components/Table';
import { UserContext } from '../../../../../context/UserContext';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { DateTimeUtil } from '../../../../../util/date.util';
import dayjs from 'dayjs';
import { Entity } from '../../../../../types/common/entity';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { LotContext } from '../../../../../context/LotContext';
import { OrderContext } from '../../../../../context/OrderContext';
import { BinContext } from '../../../../../context/BinContext';
import { BinStatusContext } from '../../../../../context/BinStatusContext';
import { ActivityUtil } from '../Util/ActivityUtil';

interface PartnerActivityPaneProps {}

export default function PartnerActivityPane({}: PartnerActivityPaneProps) {
  const { companies } = useContext(CompanyContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { productTransactions, productTransactionsLoading } = useContext(ProductTransactionContext);
  const { stockLocations, stockLocationsLoading } = useContext(StockLocationContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { lots, lotsLoading } = useContext(LotContext);
  const { orders, ordersLoading } = useContext(OrderContext);
  const { bins, binsLoading } = useContext(BinContext);
  const { binStatuses, binStatusesLoading } = useContext(BinStatusContext);

  const items = useMemo(
    () =>
      [
        ...Array.from(stockLocations.values()),
        ...Array.from(productMasterData.values()),
        ...Array.from(productTransactions.values()),
        ...Array.from(lots.values()),
        ...Array.from(orders.values()),
        ...Array.from(bins.values()),
        ...Array.from(binStatuses.values()),
      ].sort((a, b) => dayjs(b.updatedAt).unix() - dayjs(a.updatedAt).unix()),
    [stockLocations, productMasterData, productTransactions, lots, orders, bins, binStatuses],
  );

  const [filteredItems, setFilteredItems] = useState<Entity[]>([...items]);

  useEffect(() => {
    setFilteredItems([...items]);
  }, [items]);

  const loading =
    productTransactionsLoading ||
    stockLocationsLoading ||
    productMasterDataLoading ||
    lotsLoading ||
    ordersLoading ||
    binsLoading ||
    binStatusesLoading;

  const headers = [
    {
      key: 'company',
      name: t(TranslationKey.company).singular.label,
      text: (item: Entity) => {
        return companies.get(item.companyId)?.name || 'System Company';
      },
    },
    {
      key: 'updatedAt',
      name: t(TranslationKey.updatedAt).singular.label,
      text: (item: Entity) => DateTimeUtil.toLocaleDateTimeString(locale, dayjs(item.updatedAt).toDate()),
      defaultSort: true,
    },
    {
      key: 'updatedBy',
      name: t(TranslationKey.updatedBy).singular.label,
      text: (item: Entity) => {
        return companyUsers.get(item.updatedBy as string)?.email || 'System User';
      },
    },
    {
      key: 'text',
      name: '',
      text: (item: Entity) => {
        return ActivityUtil.toText(item, productMasterData, orders);
      },
    },
  ];

  return (
    <div className='h-full'>
      <Table
        loading={loading}
        items={filteredItems}
        headers={headers}
        totalItemCount={items.length}
        onClick={item => {}}
      />
    </div>
  );
}
